import Nav from "@/components/oakbridge/Nav";
import Footer from "@/components/oakbridge/Footer";
import { ExternalLink, ArrowLeft, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const PROJECTS = [
  {
    slug: "eloquent-voices",
    name: "Eloquent Voices",
    category: "Audio equipment rental",
    year: "2026",
    url: "https://eloquentvoices.co",
    image:
      "https://customer-assets.emergentagent.com/job_web-service-pro/artifacts/p2zr39rm_Capture.PNG",
    blurb:
      "A confident marketing site for a professional sound, stage and lighting rental company. Deep-green editorial layout, dedicated equipment and packages pages, and a clear quote-request path for event organisers.",
    highlights: ["Bold hero with signature product photography", "Equipment / packages / gallery sections", "Quote-request contact flow"],
  },
  {
    slug: "vote-raj",
    name: "Vote Raj Gill",
    category: "Political campaign",
    year: "2026",
    url: null,
    image:
      "https://customer-assets.emergentagent.com/job_web-service-pro/artifacts/jzqj6zeo_voteraj.PNG",
    blurb:
      "A campaign website for Raj Gill — a Central Executive Committee candidate in London. Warm cream palette with campaign-red accents, a clear voting timeline, and separate pages for policy positions, posters and voter contact.",
    highlights: ["Voting window and clear call-to-action", "Multi-section policy layout", "Downloadable posters and shareable assets"],
  },
  {
    slug: "palestine-map",
    name: "Palestine Map",
    category: "Charity / solidarity platform",
    year: "2025",
    url: "https://palestinemap.org",
    image:
      "https://customer-assets.emergentagent.com/job_web-service-pro/artifacts/5yvv8vgb_palestinemap.PNG",
    blurb:
      "A UK-wide directory of pro-Palestinian businesses with an interactive map. Search by name, filter by category and city, or explore visually — with a login flow so verified organisers can add new listings.",
    highlights: ["Interactive Leaflet map + listings view", "Search + category + city filters", "Authenticated admin flow to add businesses"],
  },
];

export default function Projects({ currency, setCurrency, rates }) {
  return (
    <main data-testid="projects-page" className="min-h-screen">
      <Nav currency={currency} setCurrency={setCurrency} rates={rates} />

      {/* Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="oak-container pt-16 md:pt-28 pb-14 md:pb-20">
          <Link
            to="/"
            data-testid="projects-back-home"
            className="inline-flex items-center gap-2 text-sm text-[#5C665F] hover:text-[#1A3622] transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="mt-8 max-w-3xl">
            <p className="text-sm uppercase tracking-[0.18em] text-[#5C665F]">Selected work</p>
            <h1
              data-testid="projects-title"
              className="font-heading font-extrabold tracking-tight leading-[1.02] text-4xl md:text-6xl text-[#1A3622] mt-4"
            >
              A few sites Oakbridg has built.
            </h1>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-[#5C665F]">
              Each of these was scoped, quoted and shipped end-to-end using the same simple
              process. Different industries, different budgets — same reliable delivery.
            </p>
          </div>
        </div>
      </section>

      {/* Project cards */}
      <section className="bg-[#FAF9F6]">
        <div className="oak-container py-20 md:py-28 space-y-16 md:space-y-24">
          {PROJECTS.map((p, i) => (
            <article
              key={p.slug}
              data-testid={`project-${p.slug}`}
              className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
            >
              {/* Image */}
              {p.url ? (
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`project-${p.slug}-image-link`}
                  className={
                    "block group relative overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white shadow-sm hover:shadow-xl transition-all " +
                    (i % 2 === 1 ? "lg:order-2" : "")
                  }
                >
                  <img
                    src={p.image}
                    alt={`${p.name} — ${p.category}`}
                    loading="lazy"
                    className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-medium text-[#1A3622] shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    Visit site <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </a>
              ) : (
                <div
                  data-testid={`project-${p.slug}-image`}
                  className={
                    "relative overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white shadow-sm " +
                    (i % 2 === 1 ? "lg:order-2" : "")
                  }
                >
                  <img
                    src={p.image}
                    alt={`${p.name} — ${p.category}`}
                    loading="lazy"
                    className="w-full h-auto object-cover"
                  />
                </div>
              )}

              {/* Content */}
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-block rounded-full bg-[#F3EFE9] border border-[#E5E0D8] text-[#1A3622] px-3 py-1 text-xs uppercase tracking-[0.14em]">
                    {p.category}
                  </span>
                  <span className="text-xs text-[#5C665F] uppercase tracking-[0.14em]">
                    {p.year}
                  </span>
                </div>
                <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mt-4 text-[#1A3622]">
                  {p.name}
                </h2>
                <p className="mt-4 text-[17px] leading-relaxed text-[#5C665F]">{p.blurb}</p>

                <ul className="mt-6 space-y-2">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-[15px] text-[#1C211E]">
                      <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[#C17F59] shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>

                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`project-${p.slug}-visit`}
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1A3622] px-6 h-11 text-sm font-medium text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:bg-[#122618]"
                  >
                    Visit live site
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="border-t border-[#E5E0D8] bg-[#1A3622] text-white">
        <div className="oak-container py-16 md:py-24">
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight text-white">
                Yours could be next.
              </h2>
              <p className="mt-4 text-[17px] text-white/80 max-w-lg">
                Bring a rough idea — Oakbridg will come back with a fixed quote and a timeline
                within a working day. Live within 7 days.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link
                to="/#inquiry"
                data-testid="projects-cta-inquiry"
                className="inline-flex items-center gap-2 rounded-full bg-[#C17F59] hover:bg-[#A86A45] px-7 h-12 text-base font-medium text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                Start a project <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
